import { getDomainName } from '@src/config';
import { PostStatus } from '@src/store/contentEntities/model';
import { isPostPublished } from '@src/store/posts/helpers/posts';
import type { PostCollectionType } from '@src/store/posts/posts';
import { getToken } from '../token/helpers';
import { getMyForwardedFromUserIds, getMyUser, getUserId, isUserModerator } from '../user/helpers';
import { hasSomeTokenPermission, isPermissioned } from '../user/permission';
import { getPost, getPostComment } from './helpers';

export function canEditPost(postId: string, tokenName: string): boolean {
    const myUser = getMyUser();
    if (!myUser) return false;

    const post = getPost(postId);
    if (!post) return false;

    const token = getToken(tokenName);
    if (!token) return false;
    if (canModeratePost(postId, tokenName)) return true;

    if (token.approvePosts && isPostPublished(post)) return false;
    if (myUser.id === post.publisherId) return true;

    const forwardedFrom = getMyForwardedFromUserIds();
    if (forwardedFrom.includes(post.publisherId)) return true;

    return false;
}

export function canModeratePost(postId: string, tokenName: string) {
    const myUser = getMyUser();
    if (!myUser) return false;

    const post = getPost(postId);
    if (!post) return false;

    if (myUser.networkAdmin) return true;
    if (post.kind === 'story' && isPermissioned(tokenName, 'hidePremiumContent')) return true;
    if (post.kind === 'community' && isPermissioned(tokenName, 'hideContent')) return true;

    return false;
}

export function canDeleteAllContent(tokenName: string) {
    const token = getToken(tokenName);
    if (!token) return false;
    return isPermissioned(tokenName, 'hidePremiumContent') && isPermissioned(tokenName, 'hideContent');
}

export function canManageDraftPost(postId: string, tokenName: string) {
    const myUser = getMyUser();
    if (!myUser) return false;

    const post = getPost(postId);
    if (!post) return false;

    const isDraft = post.status === PostStatus.DRAFT || post.status === PostStatus.PUBLISHED_SCHEDULED;
    if (!isDraft) return false;

    const token = getToken(tokenName);
    if (!token) return false;

    if (canEditPost(postId, tokenName)) return true;

    if (post.kind === 'community' && isPermissioned(tokenName, 'hideContent')) return true;

    return false;
}

export function canEditPremiumDraft(tokenName: string) {
    const myUser = getMyUser();
    if (!myUser) return false;

    return isPermissioned(tokenName, 'hidePremiumContent', myUser.id);
}

export function canEditCommunityDraft(tokenName: string) {
    const myUser = getMyUser();
    if (!myUser) return false;

    return isPermissioned(tokenName, 'hideContent', myUser.id);
}

export function getEditableDraftPostType(tokenName: string): PostCollectionType | '' {
    const _canEditPremiumDraft = canEditPremiumDraft(tokenName);
    const _canEditCommunityDraft = canEditCommunityDraft(tokenName);
    if (_canEditPremiumDraft && _canEditCommunityDraft) return 'draft';
    if (_canEditPremiumDraft) return 'premiumDraft';
    if (_canEditCommunityDraft) return 'communityDraft';
    return '';
}

export function getEditableScheduledPostType(tokenName: string): 'scheduled' | 'premiumScheduled' | 'communityScheduled' | '' {
    const _canEditPremiumDraft = canEditPremiumDraft(tokenName);
    const _canEditCommunityDraft = canEditCommunityDraft(tokenName);
    if (_canEditPremiumDraft && _canEditCommunityDraft) return 'scheduled';
    if (_canEditPremiumDraft) return 'premiumScheduled';
    if (_canEditCommunityDraft) return 'communityScheduled';
    return '';
}

export function canEditPostComment(commentId: string): boolean {
    const userId = getUserId();
    const myUser = getMyUser();
    if (!userId) return false;

    const comment = getPostComment(commentId);

    if (!comment) return false;
    if (myUser?.ForwardedFromArray?.includes(comment.publisherId)) return true;
    if (userId !== comment.publisherId) return false;

    return true;
}

export function canDeletePostComment(commentId: string, tokenName: string): boolean {
    const userId = getUserId();
    const myUser = getMyUser();

    if (!userId) return false;

    const comment = getPostComment(commentId);
    if (!comment) return false;

    const postId = comment.postId;
    const post = getPost(postId);
    if (!post) return false;

    if (myUser?.ForwardedFromArray?.includes(comment.publisherId)) return true;

    const token = getToken(tokenName);
    if (!token) return false;

    if (canModeratePost(postId, tokenName)) return true;
    if (userId === comment.publisherId) return true;

    const user = getMyUser();
    if (!user) return false;

    // if (tokenName && user.moderator && user.moderator[tokenName]) return true;
    if (user.domain === getDomainName() && user.domainAdmin) return true;
    return false;
}

export function canViewDeletedPosts(tokenName: string): boolean {
    const userId = getUserId();
    const user = getMyUser();
    const token = getToken(tokenName);

    if (token && token.creatorID === userId) return true;
    if (user?.domainAdmin) return true;
    if (isUserModerator(userId, tokenName)) return true;

    return false;
}

export function canViewPendingContent(tokenName: string): boolean {
    const userId = getUserId();
    const user = getMyUser();

    if (user?.domainAdmin) return true;
    return isPermissioned(tokenName, 'hideContent', userId);
}

export function canViewPendingComment(commentId: string): boolean {
    const userId = getUserId();
    const comment = getPostComment(commentId);
    if (!comment) return false;
    const { tokenName, publisherId } = comment;
    if (userId === publisherId) return true;
    return canViewPendingContent(tokenName);
}

export function canViewPendingTranscriptContent(postId: string): boolean {
    const userId = getUserId();

    const post = getPost(postId);
    if (!post) return false;

    return post.publisherId === userId;
}

export function canViewPostDetails(tokenName: string, postId: string): boolean {
    const myUser = getMyUser();
    if (!myUser) return false;

    const post = getPost(postId);
    if (!post) return false;

    const { status, publisherName } = post;
    const isUserPublisher = post.publisherId === myUser?.id;
    if (isUserPublisher) return true; // Publisher can always view their own post
    if (status === PostStatus.PUBLISHED || status === PostStatus.PUBLISHED_UNLISTED) return true;

    // only publisher can view deleted posts, if they deleted it themselves
    if (status === PostStatus.DELETED_BY_PUBLISHER && publisherName !== myUser?.displayName) return false;
    if (status === PostStatus.DRAFT || status === PostStatus.PUBLISHED_SCHEDULED) return canEditPost(postId, tokenName);

    const userId = getUserId();
    const token = getToken(tokenName);
    if (!token) return false;
    if (token.creatorID === userId) return true;
    if (myUser.domainAdmin) return true;
    if (isUserModerator(userId, tokenName)) return true;

    return false;
}

export function canDeletePost(postId: string): boolean {
    const myUser = getMyUser();
    if (!myUser) return false;

    const userId = getUserId();
    if (!userId) return false;

    const post = getPost(postId);
    if (!post) return false;

    const { tokenName } = post;
    const token = getToken(tokenName);
    if (!token) return false;

    const forwardedFrom = getMyForwardedFromUserIds();

    const user = getMyUser();
    if (!user) return false;

    const isPostPublisher = post.publisherId === userId || forwardedFrom.includes(post.publisherId);
    const isAdmin = canModeratePost(postId, tokenName);

    // only publisher can view deleted posts, if they deleted it themselves
    if (post.status === PostStatus.DELETED_BY_PUBLISHER && post.publisherName !== myUser?.displayName) return false;
    if (post.status === PostStatus.DRAFT || post.status === PostStatus.PUBLISHED_SCHEDULED) return canEditPost(postId, tokenName);

    if (post.status === PostStatus.DELETED_BY_TOKEN_ADMIN && !isAdmin) return false; // if post already deleted by admin, and user is nither token creator or mooderator
    if (post.status === PostStatus.DELETED_BY_PUBLISHER && !isPostPublisher) return false; // if post already deleted by post publisher, and user is not that publisher

    if (isAdmin || isPostPublisher) return true;
    return false;
}

export function canPinPost(postId: string): boolean {
    const userId = getUserId();
    const user = getMyUser();
    if (!userId || !user) return false;

    const post = getPost(postId);
    if (!post) return false;

    const { tokenName } = post;

    const token = getToken(tokenName);
    if (!token) return false;

    return isPermissioned(tokenName, 'pinPost');
}

export function canSuperComment(postId: string, entitiyType: 'post' | 'comment' = 'post'): boolean {
    const userId = getUserId();
    const user = getMyUser();
    if (!userId || !user) return false;

    const entitiy = entitiyType === 'post' ? getPost(postId) : getPostComment(postId);
    if (!entitiy) return false;

    const { publisherId } = entitiy;
    if (publisherId === 'anonymous' || publisherId === getUserId()) return false;

    return true;
}

export function canViewReports(tokenName: string) {
    return hasSomeTokenPermission(tokenName);
}
